import { DateTime } from 'luxon';
import { z } from 'zod';

import { createPaginatedResultSchema } from '@/lib/api/schemas/shared/paginated';
import {
  PaymentMethodSchema,
  PaymentMethodType,
} from '@/lib/api/schemas/sublocations';
import { zodEnum, zodTimeStamp, zodValidationDuration } from '@/utils/zodUtils';

export const VALIDATION_STATUSES = [
  'NEW',
  'RENEWED',
  'ACTIVE',
  'EXPIRED',
  'NEARING_EXPIRATION',
] as const;

export type ValidationStatus = (typeof VALIDATION_STATUSES)[number];

const ValidationAmountSchema = z.object({
  parking_fee: z.number().int().nonnegative(),
  validation_fee: z.number().int().nonnegative(),
});

export type ValidationAmountType = z.infer<typeof ValidationAmountSchema>;

export const AppliedViaEnum = z.enum(['access_code', 'manual']);
export type AppliedVia = z.infer<typeof AppliedViaEnum>;

export const ValidationSchema = z.object({
  locationId: z.string(),
  subLocationId: z.string(),
  blockFaceId: z.string().uuid().nullish(),
  sublocationDisplayName: z.string(),
  name: z.string(),
  phone: z.string(),
  plate: z.string(),
  // Usage-based immediate exits can persist PT0S (actual stay of 0s).
  // Still accepts preset literals (all_day / until_5am_next_day / usage).
  duration: zodValidationDuration,
  plateState: z.string(),
  // Levenshtein edit distance between the searched plate and this row's
  // plate. Populated by fuzzy plate searches; 0 means an exact match.
  plateLevenstianDistance: z.number().optional(),
  expiration: zodTimeStamp,
  createdAt: zodTimeStamp,
  validationId: z.string(),
  renewed: z.boolean(),
  renewedBy: z.string().optional(),
  status: z.enum(zodEnum(VALIDATION_STATUSES)),
  nearingExpirationTextSent: z.boolean().optional(),
  isUsageBased: z.preprocess(
    (val) => (val == null ? undefined : val),
    z.boolean().default(false)
  ),
  // Per-validation fee snapshot. For usage-based validations this is the
  // recomputed fee at termination time (TerminationService rewrites
  // amount.parking_fee against the actual stay duration). Omitted on the
  // wire when the API serializer drops a zero/empty Amount.
  amount: ValidationAmountSchema.optional(),
  appliedByUserId: z.string().nullish(),
  appliedVia: AppliedViaEnum.nullish(),
});

export type ValidationType = z.infer<typeof ValidationSchema>;
export const ValidationList = z.array(ValidationSchema);
export type ValidationListType = z.infer<typeof ValidationList>;

// GET /validations cursor. Mirrors validation.PaginatedValidationsStartKey on
// the Go side: (locationId, validationId, createdAt) — the existing shape kept
// when the new endpoint shipped to avoid a parallel start-key type.
export const ValidationsPaginationKeySchema = z.object({
  locationId: z.string(),
  validationId: z.string(),
  createdAt: z.string(),
});
export type ValidationsPaginationKey = z.infer<
  typeof ValidationsPaginationKeySchema
>;

export const ValidationsPaginatedResult = createPaginatedResultSchema(
  ValidationSchema,
  ValidationsPaginationKeySchema
);
export type ValidationsPaginatedResultType = z.infer<
  typeof ValidationsPaginatedResult
>;

export const ApplyValidationPayloadSchema = z.object({
  locationId: z.string().min(1),
  subLocationId: z.string().min(1),
  plate: z.string().min(1),
  plateState: z.string().length(2),
  name: z.string().max(200).optional(),
  phone: z.string().optional(),
});
export type ApplyValidationPayload = z.infer<
  typeof ApplyValidationPayloadSchema
>;

export const SetupValidationsPaymentMethodSchema = z.object({
  setup_intent_id: z.string(),
  client_secret: z.string(),
  customer_session_client_secret: z.string(),
});

export type SetupValidationsPaymentMethodSchemaType = z.infer<
  typeof SetupValidationsPaymentMethodSchema
>;

const ValidationPaymentPeriodSchema = z.object({
  start: zodTimeStamp,
  end: zodTimeStamp,
});

const MonthlyPricingBreakdownSchema = z.object({
  saas_fee: z.number().int().nonnegative(),
  tax: z.number().int().nonnegative(),
  is_minimum_fee_applied: z.boolean(),
});

const ValidationPaymentAmountSchema = z.object({
  convenience_fee: z.number().int().nonnegative(),
  processing_fee: z.number().int().nonnegative(),
  validation_fee: z.number().int().nonnegative(),
  per_validation_fee: z.number().int().nonnegative(),
  parking_fee: z.number().int().nonnegative(),
  taxes: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
  monthly_breakdown: MonthlyPricingBreakdownSchema.optional(),
});

export type MonthlyPricingBreakdownType = z.infer<
  typeof MonthlyPricingBreakdownSchema
>;

export enum ValidationPaymentStatus {
  PENDING = 'pending',
  PAID = 'paid',
  FAILED = 'failed',
  SKIPPED = 'skipped',
  DISPUTED = 'disputed',
  REFUNDED = 'refunded',
  REQUIRE_ACTION = 'require_action',
}

/** Skipped / incomplete captures may store an empty payment method (type ""). */
const ValidationPaymentMethodSchema = z.preprocess((val) => {
  if (val == null || typeof val !== 'object') {
    return null;
  }
  const type = (val as { type?: unknown }).type;
  if (
    type !== PaymentMethodType.CARD &&
    type !== PaymentMethodType.US_BANK_ACCOUNT
  ) {
    return null;
  }
  return val;
}, PaymentMethodSchema.nullable());

const ValidationPaymentSchema = z.object({
  id: z.string(),
  status: z
    .nativeEnum(ValidationPaymentStatus)
    .or(z.literal(''))
    .transform((v) => {
      if (!v) {
        // Handle previously paid payments
        return ValidationPaymentStatus.PAID;
      }

      return v;
    }),
  period: ValidationPaymentPeriodSchema,
  amount: ValidationPaymentAmountSchema,
  method: ValidationPaymentMethodSchema,
  description: z.string(),
  validations: z.number().int().nonnegative(),
  location_id: z.string(),
  sub_location_id: z.string(),
  created_at: zodTimeStamp,
  paid_at: zodTimeStamp.nullable(),
});

export type ValidationPayment = z.infer<typeof ValidationPaymentSchema>;
export type ValidationPaymentPeriod = z.infer<
  typeof ValidationPaymentPeriodSchema
>;
export type ValidationPaymentAmount = z.infer<
  typeof ValidationPaymentAmountSchema
>;

export const ValidationPaymentNextKeySchema = z.object({
  id: z.string(),
  location_id: z.string(),
  created_at: zodTimeStamp,
});
export const ValidationPaymentsPaginatedSchema = createPaginatedResultSchema(
  ValidationPaymentSchema,
  ValidationPaymentNextKeySchema
);

export type GetValidationPaymentsInput = {
  location_id: string;
  sub_location_id: string;
  limit: number;

  // Pagination key
  id?: string;
  created_at?: DateTime;
};

export {
  ValidationPaymentAmountSchema,
  ValidationPaymentPeriodSchema,
  ValidationPaymentSchema,
};

export const ValidationBillingQuoteSchema = ValidationPaymentAmountSchema;

export type ValidationBillingQuoteType = z.infer<
  typeof ValidationBillingQuoteSchema
>;

export const MonthlyParkingBillingStatementStatusSchema = z.enum([
  'pending',
  'preview',
  'receipt',
  'empty',
  'unavailable',
]);

export const MonthlyParkingBillingLineItemSchema = z.object({
  validation_id: z.string(),
  plate: z.string(),
  plate_state: z.string(),
  expired_at: z.string(),
  created_at: z.string().optional(),
  parking_fee: z.string(),
});

export const MonthlyParkingBillingSummarySchema = z.object({
  validation_count: z.number().int().nonnegative(),
  parking_fee: z.string(),
  processing_fee: z.string(),
  tax: z.string(),
  saas_fee: z.string().optional(),
  total: z.string(),
});

export const MonthlyParkingCombinedBillingDetailsSchema = z.object({
  split_billing_details: z.boolean(),
  saas_included: z.boolean(),
  parking_period_start: z.string(),
  parking_period_end: z.string(),
  parking_validation_count: z.number().int().nonnegative(),
  saas_period_start: z.string().optional(),
  saas_period_end: z.string().optional(),
  saas_validation_count: z.number().int().nonnegative().optional(),
  per_validation_saas_fee: z.string().optional(),
  // count × per-validation fee before monthly minimum floor
  saas_calculated_fee: z.string().optional(),
  saas_minimum_applied: z.boolean().optional(),
});

export const MonthlyParkingBillingStatementSchema = z.object({
  status: MonthlyParkingBillingStatementStatusSchema,
  period_start: z.string(),
  period_end: z.string(),
  charge_date: z.string().optional(),
  preview_available_at: z.string().optional(),
  payment_id: z.string().optional(),
  payment_status: z.string().optional(),
  paid_at: z.string().optional(),
  location_id: z.string(),
  location_name: z.string(),
  sub_location_id: z.string(),
  sub_location_name: z.string().optional(),
  payee_name: z.string().optional(),
  payee_email: z.string().optional(),
  summary: MonthlyParkingBillingSummarySchema,
  combined_details: MonthlyParkingCombinedBillingDetailsSchema.optional(),
  line_items: z.array(MonthlyParkingBillingLineItemSchema),
  message: z.string().optional(),
  not_yet_charged: z.boolean(),
  stripe_invoice_pdf_url: z.string().optional(),
  stripe_receipt_url: z.string().optional(),
  stripe_invoice_id: z.string().optional(),
  refund: z
    .object({
      amount: z.string(),
      refunded_at: z.string().optional(),
      refund_id: z.string().optional(),
      stripe_credit_note_id: z.string().optional(),
      stripe_credit_note_pdf_url: z.string().optional(),
    })
    .optional(),
});

export type MonthlyParkingBillingStatement = z.infer<
  typeof MonthlyParkingBillingStatementSchema
>;

export type MonthlyParkingBillingLineItem = z.infer<
  typeof MonthlyParkingBillingLineItemSchema
>;

export type GetMonthlyParkingBillingStatementInput = {
  location_id: string;
  sub_location_id: string;
  year: number;
  month: number;
  document?: 'receipt' | 'credit_note';
};
